import React, { ReactElement, useEffect, useState } from "react";
import Drawer from "../../../Common/Drawer/Drawer";
import InputText from "../../../Common/InputText";
import TextArea from "../../../Common/TextArea";
import Notification from '../../../Common/Notification';
import { Dispatch } from "redux";
import { useDispatch } from "react-redux";

import { actions as baselineTaskActions } from '../../../../actions/baselineTaskActions';
import * as types from '../../../../constants/actionTypes';
import Toggle from "../../../Common/Toggle/Toggle";
import LoadingIndicator from "../../../Common/LoadingIndicator/LoadingIndicator";

interface BaselineTaskLinkProps {
    name: string,
    url: string,
    disable: boolean
};

const DEFAULT_BASELINE_TASK_LINK: BaselineTaskLinkProps = {
    name: '',
    url: '',
    disable: false
};

const DEFAULT_PROTOCOLS: Array<any> = [
    { key: 'https', value: 'https://' },
    { key: 'http', value: 'http://' }
];

interface UpdateCrossBaselineLinkCountProps {
    baselineTaskLinkCount: number,
    locationTaskLinkCount: number
}

interface BulkUpdateAttributesDrawerProps {
    selectedLinks: Array<any>,
    // baselineTaskLink: BaselineTaskLinkProps
    onCancel: () => void,
    onComplete: () => Promise<void>
};
const BulkUpdateAttributesDrawer = (props: BulkUpdateAttributesDrawerProps): ReactElement => {
    
    const [ baselineTaskLink, setBaselineTaskLink ] = useState(DEFAULT_BASELINE_TASK_LINK);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ isFormValid, setIsFormValid ] = useState(true);
    const [ isUrlValid, setIsUrlValid ] = useState(true);
    const [ updateCount, setUpdateCount ] = useState<UpdateCrossBaselineLinkCountProps>();
    
    const dispatch: Dispatch = useDispatch();
    const updateCrossBaselineLink = async (request: any) => dispatch(baselineTaskActions.updateCrossBaselineLink(request));
    const updateCrossBaselineLinkCount = async (request: any) => dispatch(baselineTaskActions.updateCrossBaselineLinkCount(request));

    useEffect(() => {
        const getCountAsync = async () => {
            const taskLink = { baselineTaskLinkIds: props.selectedLinks.map(x => x.id) };
            const response = await updateCrossBaselineLinkCount(taskLink);

            if (response.type === types.SEARCH_CROSS_BASELINE_LINK_COUNT_SUCCESS) {
                setUpdateCount(response.payload);
            } else if (response.type === types.SEARCH_CROSS_BASELINE_LINK_COUNT_FAILURE) {
                Notification("error", "An Error Occurred", response.payload.message);
            }
        };

        if (props.selectedLinks.length > 0) {
            getCountAsync();
        } else
        {
            setUpdateCount(undefined);
            props.onComplete();
        }

    }, [props.selectedLinks])

    const validateForm = () => {
        const isFormValid = (baselineTaskLink.name && baselineTaskLink.name.trim().length)
            || (baselineTaskLink.url && baselineTaskLink.url.trim().length) 
            || baselineTaskLink.disable;

        const validUrl = (DEFAULT_PROTOCOLS.some(x => baselineTaskLink.url.startsWith(x.key))) && (baselineTaskLink.url.includes('//'));

        setIsUrlValid(validUrl);

        return isFormValid && validUrl;
    }

    const onSave = async () => {
        setIsLoading(true);

        const { name, url, disable } = baselineTaskLink;
        const baselineTaskLinkIds = props.selectedLinks.map(x => x.id);
        const taskLink = { name, url, disable, baselineTaskLinkIds };

        try {

            if (validateForm())
            {
                setIsFormValid(true);

                const response = await updateCrossBaselineLink(taskLink);
    
                if (response.type === types.UPDATE_CROSS_BASELINE_LINK_SUCCESS) {
                    Notification("success", "Success", `Baseline Task Links ${disable ? 'disabled' : 'updated'}`);
                    props.onComplete();
                } else if (response.type === types.UPDATE_CROSS_BASELINE_LINK_FAILURE) {
                    Notification("error", "An Error Occurred", response.payload.message);
                }
            } else {
                setIsFormValid(false);
            }
        } catch (error) {
            Notification("error", "An Error Occurred", error);
        }
        setIsLoading(false);
    };

    const onAbbreviationChange = (event: any) => {
        const { value } = event.target;
        setBaselineTaskLink(prev => ({
            ...prev,
            name: value
        }));
    };

    const onURLChange = (event: any) => {
        const { value } = event.target;

        const validUrl = (DEFAULT_PROTOCOLS.some(x => value.startsWith(x.key))) && (value.includes('//'));
        setIsUrlValid(validUrl);

        setBaselineTaskLink(prev => ({
            ...prev,
            url: value
        }));
    };

    const onDisableChange = (event: any) => {
        setBaselineTaskLink(prev => ({
            ...prev,
            disable: !prev.disable
        }));
    }

    return <Drawer isSaveDisabled={isLoading} isWide={false} title='Update Links'
        onSaveClick={onSave}
        onCancelClick={props.onCancel}
        saveText='Save'>
            <LoadingIndicator isLoading={isLoading} hideWhileLoading={true} isFullscreen={true} >
                <form noValidate className={isFormValid ? "row" : "row was-validated"}>
                    <div className="col-12 float-end">
                        <Toggle 
                            name="disable"
                            text="Disable Links"
                            checked={baselineTaskLink.disable} onChange={onDisableChange} />
                    </div>

                    { !baselineTaskLink.disable &&
                        <>  
                            <div className="col-12">
                                <h6 className="text-dark">Update Links</h6>
                            </div>

                            <div className="col-12">
                                <InputText name="Abbreviation" label="New Abbreviation" placeholder="Task Link Abbreviation" type="text"
                                    value={baselineTaskLink.name}
                                    onChange={onAbbreviationChange}
                                    isRequired={true}
                                    pattern=".*\S+.*"
                                    errorMessage="New Task Link Abbreviation is required"
                                />
                            </div>

                            <div className="col-12">
                                <TextArea name="URL" label="New URL" placeholder="Task Link URL"
                                    value={baselineTaskLink.url}
                                    onChange={onURLChange}
                                    isRequired={true}
                                    pattern=".*\S+.*"
                                    errorMessage="Task Link URL is required"
                                    maxLength={500} />

                                { !isUrlValid &&
                                    <div className="alert alert-danger" role="alert">
                                        <p>Please include the entire URL including https:// or http://</p>
                                        <ul>
                                            { DEFAULT_PROTOCOLS.map(x => {
                                                return <li key={x.key}>{x.value}www.getpacer.com</li>
                                            }) }
                                        </ul>
                                    </div>
                                }
                            </div>
                        </>
                    }

                    { updateCount &&
                        <>
                            <div className="col-12">
                                <div className="alert alert-warning" role="alert">
                                    A total of <b>{updateCount.baselineTaskLinkCount} baseline task links will be {baselineTaskLink.disable ? "disabled" : "updated"}</b>
                                </div>
                            </div>

                            <div className="col-12">
                                <div className="alert alert-warning" role="alert">
                                    A total of <b>{updateCount.locationTaskLinkCount} location task links will be {baselineTaskLink.disable ? "disabled" : "updated"}</b>
                                </div>
                            </div>
                        </>
                    }
                </form>
            </LoadingIndicator>
    </Drawer>;
};

export default BulkUpdateAttributesDrawer;